import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, UserRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * One-click switcher between a staff session and that staff's shadow "personal"
 * client account.  Staff users see "Switch to Personal Account"; personal
 * sessions get a bright banner + "Back to Admin" button.
 */
export default function AccountSwitcher() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const STAFF = ["super_admin", "admin", "credit_specialist", "team_member", "tradeline_admin"];

  const goPersonal = async () => {
    setBusy(true);
    try {
      // Stash the current (admin) token so "Back to Admin" is instant + offline-safe
      const current = localStorage.getItem("cq_token");
      if (current) localStorage.setItem("cq_admin_stash", current);
      if (user?.role) localStorage.setItem("cq_admin_stash_role", user.role);
      const { data } = await api.post("/auth/personal-account/switch");
      if (data?.token) localStorage.setItem("cq_token", data.token);
      toast.success("Switched to your personal account");
      await refresh();
      nav("/portal");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not switch");
    } finally { setBusy(false); }
  };

  const backToAdmin = async () => {
    setBusy(true);
    try {
      const stashed = localStorage.getItem("cq_admin_stash");
      const stashedRole = localStorage.getItem("cq_admin_stash_role");
      if (stashed) {
        localStorage.setItem("cq_token", stashed);
        localStorage.removeItem("cq_admin_stash");
        localStorage.removeItem("cq_admin_stash_role");
      } else {
        // Fallback — ask the server to mint a fresh token for the owning staff account
        const { data } = await api.post("/auth/personal-account/return");
        if (data?.token) localStorage.setItem("cq_token", data.token);
      }
      toast.success("Back to admin");
      await refresh();
      nav(stashedRole === "tradeline_admin" ? "/tradeline-admin" : "/admin");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not switch back");
    } finally { setBusy(false); }
  };

  if (user.is_personal_shadow || user.owner_staff_id) {
    return (
      <Button
        size="sm"
        onClick={backToAdmin}
        disabled={busy}
        className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
        data-testid="switch-back-to-admin"
      >
        <ShieldCheck size={14} className="mr-1" /> Back to Admin
      </Button>
    );
  }

  if (STAFF.includes(user.role)) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={goPersonal}
        disabled={busy}
        className="rounded-xl border-amber-200 text-amber-700 hover:bg-amber-50"
        data-testid="switch-to-personal"
        title="Try Credit Quack as if you were a client"
      >
        <ArrowLeftRight size={14} className="mr-1" /> <span className="hidden sm:inline">Personal Account</span><span className="sm:hidden">Personal</span>
      </Button>
    );
  }

  return null;
}

/** Full-width banner shown at the top of any page while in personal mode. */
export function PersonalModeBanner() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!user || !(user.is_personal_shadow || user.owner_staff_id)) return null;

  const backToAdmin = async () => {
    setBusy(true);
    try {
      const stashed = localStorage.getItem("cq_admin_stash");
      const stashedRole = localStorage.getItem("cq_admin_stash_role");
      if (stashed) {
        localStorage.setItem("cq_token", stashed);
        localStorage.removeItem("cq_admin_stash");
        localStorage.removeItem("cq_admin_stash_role");
      } else {
        const { data } = await api.post("/auth/personal-account/return");
        if (data?.token) localStorage.setItem("cq_token", data.token);
      }
      toast.success("Back to admin");
      await refresh();
      nav(stashedRole === "tradeline_admin" ? "/tradeline-admin" : "/admin");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not switch back");
    } finally { setBusy(false); }
  };

  return (
    <div className="w-full bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 text-white" data-testid="personal-mode-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 text-sm font-semibold">
        <UserRound size={14} />
        <span>You're viewing Credit Quack as a client (your personal account).</span>
        <button
          onClick={backToAdmin}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1 rounded-lg bg-white/15 hover:bg-white/25 px-3 py-1 transition"
          data-testid="banner-back-to-admin"
        >
          <ShieldCheck size={14} /> {busy ? "…" : "Back to admin"}
        </button>
      </div>
    </div>
  );
}
